import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ShieldCheck, Loader2 } from 'lucide-react';
import axios from 'axios';

const Pricing = ({ onLogin }) => { 
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); 

  const handleSelectFree = () => {
    const username = localStorage.getItem('user');
    const role = localStorage.getItem('role');
    if (username && role) {
      onLogin(username, role);
      navigate('/');
    } else {
      navigate('/login');
    }
  };

  const handleSelectPlan = async (planName) => {
    if (planName === 'FREE') {
      handleSelectFree();
      return;
    }

    setLoading(planName);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        'https://ai-noc.skynix.co/api/subscription/create-checkout', 
        { plan: planName }, 
        { headers: { 'X-NOC-Token': token } }
      );
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error("Stripe Error:", e);
      alert("Failed to initialize payment. Please try again or contact support.");
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      title: 'EARLY ACCESS',
      name: 'FREE',
      price: '$0',
      period: '/month',
      desc: 'Full access during development',
      features: ['All core features', 'Up to 3 servers (1 year limit)', '1,000,000 AI Analysis tokens', 'Early adopter access'],
      buttonText: 'GET EARLY ACCESS',
      highlight: false, 
      active: true,
    },
    {
      title: 'BASIC',
      name: 'BASIC',
      price: '$10',
      period: '/month',
      desc: 'Up to 20 servers. Perfect for small teams',
      features: ['1,000,000 AI Analysis tokens', 'Full monitoring', 'AI alerts', 'Weekly reports'],
      buttonText: 'START BASIC',
      highlight: false,
      active: true, 
    },
    {
      title: 'MOST POPULAR',
      name: 'MIDDLE',
      price: '$20',
      period: '/month',
      desc: 'Up to 100 servers. For growing infrastructure',
      features: ['5,000,000 AI Analysis tokens', 'Everything in Basic', 'Priority processing', 'Advanced insights'],
      buttonText: 'START MIDDLE',
      highlight: true, 
      active: true,
    },
    {
      title: 'MAX',
      name: 'MAX',
      price: '$50',
      period: '/month',
      desc: '100+ servers. Enterprise-scale monitoring',
      features: ['25,000,000 AI Analysis tokens', 'Everything in Middle', 'High-scale infrastructure support', 'Priority alerts'],
      buttonText: 'START MAX',
      highlight: false,
      active: true,
    }
  ];

  return (
    <div className="vh-100 d-flex flex-column align-items-center justify-content-center" style={{ background: '#0b0e14', color: '#e2e8f0', padding: '20px', overflowY: 'auto' }}>
      <div className="text-center mb-5 mt-5">
        <div className="mb-3 d-inline-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', background: 'rgba(0, 242, 255, 0.1)', borderRadius: '10px', color: '#00f2ff' }}>
          <ShieldCheck size={30} />
        </div>
        <h2 className="fw-bold">Select Your Subscription Plan</h2>
        <p className="text-secondary small">Choose a plan to activate your SOC Dashboard</p>
      </div>

      <div className="row g-4 justify-content-center w-100 mb-5" style={{ maxWidth: '1200px' }}>
        {plans.map((plan, idx) => (
          <div key={idx} className="col-lg-3 col-md-6">
            <div style={{
              background: '#0d1117',
              border: plan.highlight ? '2px solid #00f2ff' : '1px solid #1e293b',
              borderRadius: '4px',
              padding: '30px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: plan.highlight ? '0 0 20px rgba(0, 242, 255, 0.1)' : 'none',
              transition: '0.3s'
            }}>
              <div style={{ fontSize: '10px', color: '#00f2ff', letterSpacing: '2px', marginBottom: '10px', fontWeight: 'bold' }}>{plan.title}</div>
              
              <h4 className="fw-bold mb-0">{plan.name}</h4>
              <div className="d-flex align-items-baseline mt-2">
                <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#fff' }}>{plan.price}</span>
                <span className="text-secondary small ms-1">{plan.period}</span>
              </div>
              
              <p className="small text-secondary mt-3 mb-4" style={{ minHeight: '40px' }}>{plan.desc}</p>
              
              <div className="flex-grow-1">
                {plan.features.map((f, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-2 small text-secondary">
                    <Check size={14} className="text-info" /> {f}
                  </div>
                ))}
              </div>

              <button 
                onClick={() => handleSelectPlan(plan.name)}
                disabled={loading !== null}
                className={`btn w-100 mt-4 fw-bold py-2 d-flex align-items-center justify-content-center gap-2 ${plan.highlight ? 'btn-info shadow-info' : 'btn-outline-info'}`}
                style={{ 
                    fontSize: '11px', 
                    letterSpacing: '1px', 
                    background: plan.highlight ? '#00f2ff' : 'transparent', 
                    color: plan.highlight ? '#0b0e14' : '#00f2ff',
                    border: '1px solid #00f2ff'
                }}
              >
                {loading === plan.name ? <Loader2 size={16} className="animate-spin" /> : plan.buttonText}
              </button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .shadow-info { box-shadow: 0 0 15px rgba(0, 242, 255, 0.3); }
      `}</style>
    </div>
  );
};

export default Pricing;